import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { Moment } from 'moment';
import moment from 'moment/moment';
import { MenuItem } from '@headlessui/react';

import { activateExtensionProfile } from 'flow/actions/extension-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { selectExtensionActiveProfile } from 'flow/selectors/ExtensionSelectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';

import { TypedMap } from 'flow/types/TypedMap';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import {
    getStandardProfileDescriptionInUserLanguage,
    resolveDefaultDuration,
} from 'flow/utils/profile-utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    extensionKey: ExtensionKey;
}

const AgentProfilePicker = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const { profiles: ownProfiles } = useProfiles(extensionKey);

    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extensionKey),
    );

    const activate = (
        profile: TypedMap<Profile>,
        until: Moment,
        event: React.MouseEvent<HTMLDivElement, MouseEvent> | null = null,
    ) => {
        event?.stopPropagation();

        /** Flag every explicit selection, regardless of the current
         * (possibly stale) pause state. The flag means "the supervisor chose
         * this profile"; the backend decides whether a pause needs cancelling.
         * Gating on a client-side "is paused" check would risk omitting the flag
         * during the exact client/server race this fix addresses.
         * **/
        const updatedProfileDTO = profile
            .update('active', () => true)
            .update('activeUntil', () =>
                until ? until.format(ISO_8601_MS_RESOLUTION) : null,
            )
            .set('explicitSelection', true);
        dispatch(activateExtensionProfile(extensionKey, updatedProfileDTO));
    };

    const handleClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        profile: TypedMap<Profile>,
        close: () => void,
    ) => {
        event.stopPropagation();
        openModal('setProfileUntil', {
            profile,
            onSubmit: activate,
        });
        close();
    };

    const sortedProfiles = ownProfiles?.sort((profileA, profileB) => {
        const isProfileAActive =
            profileA.get('key') === activeProfile.get('key');
        const isProfileBActive =
            profileB.get('key') === activeProfile.get('key');
        return isProfileAActive ? -1 : isProfileBActive ? 1 : 0;
    });

    return (
        <>
            {sortedProfiles?.map((profile) => {
                const until = resolveDefaultDuration(
                    profile.get('defaultDuration'),
                    moment(),
                );

                return (
                    <MenuItem key={`eps-${profile.get('key')}`}>
                        {({ close, active }) => (
                            <div
                                className={twMerge(
                                    'group/submenu cursor-pointer rounded-lg px-2 py-1 select-none hover:bg-gray-200',
                                    active ? 'bg-gray-200' : '',
                                )}
                                onClick={(event) =>
                                    activate(profile, until, event)
                                }
                            >
                                <div className="flex items-center gap-x-3">
                                    <div className="flex h-8 flex-1 items-center">
                                        <p
                                            dir="auto"
                                            className={twMerge(
                                                'truncate text-base',
                                                profile.get('key') ===
                                                    activeProfile.get('key')
                                                    ? 'font-medium'
                                                    : 'font-normal',
                                            )}
                                        >
                                            {getStandardProfileDescriptionInUserLanguage(
                                                profile.get('description'),
                                                i18next,
                                            )}
                                        </p>
                                    </div>

                                    {profile.get('plusId') !== 0 ? (
                                        <IconButton
                                            aria-label={i18next.t(
                                                'profile.set_end_time',
                                            )}
                                            onClick={(event) =>
                                                handleClick(
                                                    event,
                                                    profile,
                                                    close,
                                                )
                                            }
                                            className="invisible group-hover/submenu:visible"
                                        >
                                            <Icon iconKey="accessTime" />
                                        </IconButton>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </MenuItem>
                );
            })}
        </>
    );
};

export default React.memo(AgentProfilePicker);
